/* ---------------------------------------------------------------------
 * ImportMeetingsDialog.tsx
 * Drag-and-drop dialog for bulk-importing meetings from CSV/XLSX.
 * ------------------------------------------------------------------- */

import { useState, useCallback } from "react";
import {
  ApiError,
  ImportsService,
  OrganizationResponseForAdmins,
} from "client/openapi";
import Notifications from "util/notifications";
import { announceMeetingsChanged } from "util/meetingsChanged";
import { PlusIcon, UploadIcon } from "@radix-ui/react-icons";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import { DialogContent } from "components/Dialog";
import "./index.css";

export default function ImportMeetingsDialog({
  organizationId,
  organization,
  onSuccess,
}: {
  organizationId: number;
  organization?: OrganizationResponseForAdmins;
  onSuccess?: () => void;
}) {
  /* ------------------------------------------------------------------ */
  /* State + drag-and-drop handlers                                     */
  /* ------------------------------------------------------------------ */

  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const [file] = Array.from(e.dataTransfer.files);
    if (file) handleFileSelect(file);
  }, []);

  /* ------------------------------------------------------------------ */
  /* File-selection helpers                                              */
  /* ------------------------------------------------------------------ */

  const handleFileSelect = (file: File) => {
    const allowed = [
      "text/csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!allowed.includes(file.type)) {
      Notifications.error("Please select a CSV or Excel file");
      return;
    }
    setUploadedFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  /* ------------------------------------------------------------------ */
  /* Download template                                                  */
  /* ------------------------------------------------------------------ */

  const downloadTemplate = async () => {
    const columns = [
      "Start",
      "Duration",
      "Name (optional)",
      "Subject Name",
      "Location Name (optional)",
      "Students",
      "Tutors",
      "Frequency",
      "End Date (required for recurring)",
    ];

    /* Build valid-values lists from org data */
    const subjectNames = (organization?.subjects ?? [])
      .filter((s) => s.active)
      .map((s) => s.name);

    const locationNames = (organization?.locations ?? []).map((l) => l.name);

    const frequencyValues = [
      "single",
      "weekly",
      "biweekly",
      "every_21_days",
      "monthly",
    ];

    /* Example row uses the first real subject/location if available */
    const examples = [
      "2025-01-15 2:00 PM",
      "60",
      "Math Tutoring",
      subjectNames[0] || "Algebra",
      locationNames[0] || "Main Office",
      "John Doe, Jane Smith",
      "Andrew Tutor, Alex Tutor",
      "weekly",
      "2025-06-15",
    ];

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Meetings Import Template");

    const patternFill = (argb: string): ExcelJS.FillPattern => ({
      type: "pattern",
      pattern: "solid",
      fgColor: { argb },
    });

    const headerStyle: Partial<ExcelJS.Style> = {
      fill: patternFill("FF366092"),
      font: { color: { argb: "FFFFFFFF" }, bold: true },
      alignment: { horizontal: "center" as const },
    };

    const dataStyle: Partial<ExcelJS.Style> = {
      fill: patternFill("FFFFFFFF"),
      font: { color: { argb: "FF000000" } },
    };

    const refHeaderStyle: Partial<ExcelJS.Style> = {
      fill: patternFill("FF8DB4E2"),
      font: { color: { argb: "FFFFFFFF" }, bold: true },
      alignment: { horizontal: "left" as const },
    };

    const refValueStyle: Partial<ExcelJS.Style> = {
      fill: patternFill("FFE8F0FE"),
      font: { color: { argb: "FF333333" } },
      alignment: { horizontal: "left" as const },
    };

    /* ---- Header + example rows ---- */

    const headerRow = ws.addRow(columns);
    const exampleRow = ws.addRow(examples);

    headerRow.eachCell((cell) => {
      cell.style = headerStyle;
    });

    exampleRow.eachCell((cell) => {
      cell.style = dataStyle;
    });

    /* ---- Column widths ---- */

    columns.forEach((c, i) => {
      ws.getColumn(i + 1).width = Math.max(c.length + 4, 18);
    });

    /* ---- Data validation for Frequency (rows 3-100) ---- */

    const freqColIdx = columns.indexOf("Frequency") + 1;
    for (let row = 3; row <= 100; row++) {
      ws.getCell(row, freqColIdx).dataValidation = {
        type: "list",
        allowBlank: false,
        formulae: [`"${frequencyValues.join(",")}"`],
      };
    }

    /* ---- Data validation for Subject Name if subjects exist ---- */

    if (subjectNames.length > 0) {
      const subjectColIdx = columns.indexOf("Subject Name") + 1;
      for (let row = 3; row <= 100; row++) {
        ws.getCell(row, subjectColIdx).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [`"${subjectNames.join(",")}"`],
        };
      }
    }

    /* ---- Data validation for Location Name if locations exist ---- */

    if (locationNames.length > 0) {
      const locationColIdx = columns.indexOf("Location Name (optional)") + 1;
      for (let row = 3; row <= 100; row++) {
        ws.getCell(row, locationColIdx).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [`"${locationNames.join(",")}"`],
        };
      }
    }

    /* ---- Reference section (off to the right, outside import columns) ---- */

    const refStartCol = columns.length + 2; // one gap column

    const refSections: { title: string; values: string[] }[] = [
      { title: "Valid Subjects", values: subjectNames },
      { title: "Valid Locations", values: locationNames },
      { title: "Valid Frequencies", values: frequencyValues },
    ];

    let currentCol = refStartCol;
    for (const section of refSections) {
      if (section.values.length === 0) continue;

      // Header cell
      const headerCell = ws.getCell(1, currentCol);
      headerCell.value = section.title;
      headerCell.style = refHeaderStyle;

      // Value cells
      section.values.forEach((val, i) => {
        const cell = ws.getCell(i + 2, currentCol);
        cell.value = val;
        cell.style = refValueStyle;
      });

      // Width
      const maxLen = Math.max(
        section.title.length,
        ...section.values.map((v) => v.length)
      );
      ws.getColumn(currentCol).width = maxLen + 4;

      currentCol++;
    }

    /* ---- Write + download ---- */

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "meetings_import_template.xlsx"
    );
  };

  /* ------------------------------------------------------------------ */
  /* Upload handler                                                     */
  /* ------------------------------------------------------------------ */

  const handleUpload = async () => {
    if (!uploadedFile) {
      Notifications.error("Please select a file first");
      return;
    }

    setIsUploading(true);

    try {
      const response = await ImportsService.bulkImportMeetings({
        orgId: organizationId,
        formData: { file: uploadedFile },
      });

      announceMeetingsChanged();
      Notifications.success(
        `Import completed! ${response.meetings_created} meeting(s) created.`
      );
      if (response.rows_skipped_as_duplicates > 0) {
        Notifications.success(
          `${response.rows_skipped_as_duplicates} duplicate row(s) skipped.`
        );
      }

      setUploadedFile(null);
      onSuccess?.();
    } catch (err) {
      const e = err as ApiError;
      if (Array.isArray(e.body?.detail)) {
        e.body.detail.forEach((msg: string) => Notifications.error(msg));
      } else {
        Notifications.error(
          e.body?.detail || "An error occurred during import"
        );
      }
      console.error(`Error (#${e.status}):`, e.body?.detail);
    } finally {
      setIsUploading(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* JSX                                                                */
  /* ------------------------------------------------------------------ */

  return (
    <DialogContent className="dialog-content">
      <div className="bulk-import--dialog-header">
        <h3>Import Meetings</h3>
        <p>Upload a CSV or Excel file to bulk-import meetings</p>
        <p className="bulk-import--template-link">
          Need a template?{" "}
          <button
            type="button"
            onClick={downloadTemplate}
            className="bulk-import--template-btn"
          >
            Click here to download.
          </button>
        </p>
      </div>

      <div
        className={`bulk-import--drop-zone ${
          isDragOver ? "bulk-import--drag-over" : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="bulk-import--content">
          {uploadedFile ? (
            <div className="bulk-import--file-selected">
              <div className="bulk-import--file-info">
                <UploadIcon className="bulk-import--file-icon" />
                <span className="bulk-import--file-name">
                  {uploadedFile.name}
                </span>
                <span className="bulk-import--file-size">
                  ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
                </span>
              </div>
              <button
                className="bulk-import--upload-btn"
                onClick={handleUpload}
                disabled={isUploading}
              >
                {isUploading ? (
                  <div className="bulk-import--spinner">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    <span>Uploading...</span>
                  </div>
                ) : (
                  <span>Submit</span>
                )}
              </button>
            </div>
          ) : (
            <div className="bulk-import--empty-state">
              <div className="bulk-import--upload-icon">
                <PlusIcon />
              </div>
              <div className="bulk-import--text">
                <p className="bulk-import--title">Drag and drop your file</p>
                <p className="bulk-import--subtitle">or</p>
                <label className="bulk-import--file-input-label">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileInput}
                    className="bulk-import--file-input"
                  />
                  <span className="bulk-import--browse-btn">Browse Files</span>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </DialogContent>
  );
}
